import { ArrowDown, ArrowUp, HelpCircle, MessageSquareQuote, Plus, Trash2 } from "lucide-react";
import { Box, TextField, inputClass } from "./fields";

const MAX_ITEMS = 12;

// Lista editável genérica: cada elemento é um objeto com os campos indicados
function ItemList({ items, onChange, fields, addLabel, empty }) {
  const update = (i, changes) => onChange(items.map((it, idx) => (idx === i ? { ...it, ...changes } : it)));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, delta) => {
    const target = i + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[i], next[target]] = [next[target], next[i]];
    onChange(next);
  };
  const blank = Object.fromEntries(fields.map((f) => [f.key, ""]));

  return (
    <div className="space-y-3">
      {items.length === 0 && <p className="text-[#8A8A8A] text-xs">{empty}</p>}
      {items.map((item, i) => (
        <div key={i} className="bg-[#141414] border border-[#3A3A3A] rounded p-3 space-y-2">
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-[#A1A1AA] disabled:opacity-30" aria-label="Subir"><ArrowUp size={16} /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-[#A1A1AA] disabled:opacity-30" aria-label="Descer"><ArrowDown size={16} /></button>
            <button type="button" onClick={() => remove(i)} className="text-red-400" aria-label="Remover"><Trash2 size={16} /></button>
          </div>
          {fields.map((f) =>
            f.multiline ? (
              <textarea key={f.key} rows={3} value={item[f.key] || ""} onChange={(e) => update(i, { [f.key]: e.target.value })} placeholder={f.placeholder} className={inputClass} />
            ) : (
              <input key={f.key} value={item[f.key] || ""} onChange={(e) => update(i, { [f.key]: e.target.value })} placeholder={f.placeholder} className={inputClass} />
            ),
          )}
        </div>
      ))}
      {items.length < MAX_ITEMS && (
        <button type="button" onClick={() => onChange([...items, blank])} className="flex items-center gap-1 bg-[#3A3A3A] hover:bg-[#4A4A4A] text-[#F5F5F5] px-3 py-2 rounded text-sm">
          <Plus size={16} /> {addLabel}
        </button>
      )}
    </div>
  );
}

// Separador "Testemunhos & FAQ": as duas secções só aparecem no site quando têm pelo menos um elemento
export default function TestimonialsFaqTab({ content, setContent }) {
  const setList = (key) => (items) => setContent((prev) => ({ ...prev, [key]: items }));
  return (
    <div className="space-y-6">
      <Box title="Testemunhos" icon={MessageSquareQuote} hint="Frases curtas de clientes, com autorização deles. Nome e texto são obrigatórios.">
        <TextField label="Etiqueta (por cima do título)" field="testimonials_eyebrow" content={content} setContent={setContent} />
        <TextField label="Título" field="testimonials_title" content={content} setContent={setContent} />
        <ItemList
          items={content.testimonials || []}
          onChange={setList("testimonials")}
          addLabel="Adicionar testemunho"
          empty="Sem testemunhos: a secção não aparece no site."
          fields={[
            { key: "text", placeholder: "O que a pessoa disse, nas palavras dela", multiline: true },
            { key: "name", placeholder: "Nome (ex.: Ana S.)" },
            { key: "detail", placeholder: "Detalhe opcional (ex.: treina connosco há 2 anos)" },
          ]}
        />
      </Box>

      <Box title="Perguntas frequentes" icon={HelpCircle} hint="Ex.: Como é a aula experimental? O que devo levar? Preciso de experiência? Há estacionamento?">
        <TextField label="Etiqueta (por cima do título)" field="faq_eyebrow" content={content} setContent={setContent} />
        <TextField label="Título" field="faq_title" content={content} setContent={setContent} />
        <ItemList
          items={content.faq || []}
          onChange={setList("faq")}
          addLabel="Adicionar pergunta"
          empty="Sem perguntas: a secção não aparece no site."
          fields={[
            { key: "question", placeholder: "Pergunta (ex.: Como é a aula experimental?)" },
            { key: "answer", placeholder: "Resposta curta e direta", multiline: true },
          ]}
        />
      </Box>
    </div>
  );
}
